import { useEffect, useState } from "react";
import {
    Badge,
    Card,
    CardBody,
    Input,
    Spinner,
    Table
} from "reactstrap";
import { CoordinatorLearningLocationDto, LearningMode, LearningRequestStatus } from "../../types/GroupLearningLocation";
import GroupLearningLocationService from "../../Services/GroupLearningLocationService";
import "./CoordinatorLearningRequestPage.css";


interface Props {
    groupId: string;
}

const CoordinatorLearningRequestPage = ({
    groupId
}: Props) => {

    const [loading, setLoading] = useState(true);

    const [requests, setRequests] =
        useState<CoordinatorLearningLocationDto[]>([]);

    useEffect(() => {

        loadRequests();

        // eslint-disable-next-line
    }, [groupId]);

    const loadRequests = async () => {

        try {

            setLoading(true);

            const data =
                await GroupLearningLocationService.getCoordinatorList(groupId);

            setRequests(data);

        }
        catch (err) {

            console.error(err);

        }
        finally {

            setLoading(false);

        }

    };

    const updateStatus = async (
        id: string,
        status: LearningRequestStatus
    ) => {

        try {

            await GroupLearningLocationService.updateStatus(
                id,
                status
            );

            setRequests(prev =>
                prev.map(x =>
                    x.groupLearningLocationId === id
                        ? {
                            ...x,
                            status
                        }
                        : x
                )
            );

        }
        catch (err) {

            console.error(err);

        }

    };

    const getBadgeColor = (
        status: LearningRequestStatus
    ) => {

        switch (status) {

            case LearningRequestStatus.Pending:
                return "warning";

            case LearningRequestStatus.Contacted:
                return "info";

            case LearningRequestStatus.MeetingScheduled:
                return "primary";

            case LearningRequestStatus.BatchAssigned:
                return "success";

            case LearningRequestStatus.Completed:
                return "dark";

            default:
                return "secondary";

        }

    };

    if (loading)
        return (
            <div className="text-center py-5">
                <Spinner />
            </div>
        );

    return (

   <>
    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h4 className="mb-2 mb-md-0">
            Learning Requests
        </h4>

        <Badge color="primary" pill>
            {requests.length} Requests
        </Badge>
    </div>

    {/* Desktop Table */}

    <div className="desktop-learning-table">

        <Table bordered hover responsive className="align-middle">

            <thead className="table-primary">

                <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Mode</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th >Update</th>
                </tr>

            </thead>

            <tbody>

                {requests.length === 0 ? (

                    <tr>
                        <td colSpan={7} className="text-center">
                            No learning requests found.
                        </td>
                    </tr>

                ) : (

                    requests.map((item) => (

                        <tr key={item.groupLearningLocationId}>

                            <td>{item.userName}</td>

                            <td>{item.email}</td>

                            <td>{item.phoneNumber}</td>

                            <td>
                                {item.learningMode === LearningMode.Online
                                    ? "🌐 Online"
                                    : "📍 In Person"}
                            </td>

                            <td>
                                {item.learningMode === LearningMode.Online
                                    ? "Online"
                                    : `${item.district}, ${item.state}`}
                            </td>

                            <td>
                                <Badge color={getBadgeColor(item.status)}>
                                    {item.status}
                                </Badge>
                            </td>

                            <td>

                                <Input
                                    type="select"
                                    value={item.status}
                                    onChange={(e) =>
                                        updateStatus(
                                            item.groupLearningLocationId,
                                            e.target.value as LearningRequestStatus
                                        )
                                    }
                                >
                                    <option value={LearningRequestStatus.Pending}>
                                        Pending
                                    </option>

                                    <option value={LearningRequestStatus.Contacted}>
                                        Contacted
                                    </option>

                                    <option value={LearningRequestStatus.MeetingScheduled}>
                                        Meeting Scheduled
                                    </option>

                                    <option value={LearningRequestStatus.BatchAssigned}>
                                        Batch Assigned
                                    </option>

                                    <option value={LearningRequestStatus.Completed}>
                                        Completed
                                    </option>

                                </Input>

                            </td>

                        </tr>

                    ))

                )}

            </tbody>

        </Table>

    </div>

    {/* Mobile Cards */}

    <div className="mobile-learning-cards">

        {requests.length === 0 ? (

            <Card className="text-center p-4">
                No learning requests found.
            </Card>

        ) : (

            requests.map((item) => (

                <Card
                    key={item.groupLearningLocationId}
                    className="learning-card shadow-sm border-0 mb-3"
                >

                    <CardBody>

                        <div className="d-flex justify-content-between align-items-center mb-3">

                            <h6 className="fw-bold text-primary mb-0">
                                {item.userName}
                            </h6>

                            <Badge color={getBadgeColor(item.status)}>
                                {item.status}
                            </Badge>

                        </div>

                        <div className="learning-row">
                            <span>Email</span>
                            <strong>{item.email}</strong>
                        </div>

                        <div className="learning-row">
                            <span>Phone</span>
                            <strong>{item.phoneNumber}</strong>
                        </div>

                        <div className="learning-row">
                            <span>Mode</span>
                            <strong>
                                {item.learningMode === LearningMode.Online
                                    ? "🌐 Online"
                                    : "📍 In Person"}
                            </strong>
                        </div>

                        <div className="learning-row">
                            <span>Location</span>

                            <strong>
                                {item.learningMode === LearningMode.Online
                                    ? "Online"
                                    : `${item.district}, ${item.state}`}
                            </strong>
                        </div>

                        <div className="mt-3">

                            <label className="fw-semibold mb-2">
                                Update Status
                            </label>

                            <Input
                                type="select"
                                value={item.status}
                                onChange={(e) =>
                                    updateStatus(
                                        item.groupLearningLocationId,
                                        e.target.value as LearningRequestStatus
                                    )
                                }
                            >

                                <option value={LearningRequestStatus.Pending}>
                                    Pending
                                </option>

                                <option value={LearningRequestStatus.Contacted}>
                                    Contacted
                                </option>

                                <option value={LearningRequestStatus.MeetingScheduled}>
                                    Meeting Scheduled
                                </option>

                                <option value={LearningRequestStatus.BatchAssigned}>
                                    Batch Assigned
                                </option>

                                <option value={LearningRequestStatus.Completed}>
                                    Completed
                                </option>

                            </Input>

                        </div>

                    </CardBody>

                </Card>

            ))

        )}

    </div>

</>

    );

};

export default CoordinatorLearningRequestPage;